import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';

import { WorkflowHeaderComponent } from './workflow-wizard/components/workflow-header/workflow-header.component';
import { WorkflowStepperComponent } from './workflow-stepper';
import { RequestService } from '@core/services/request/request.service';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { DeviceDetectorService } from '@core/services/device-detector/device-detector.service';
import {
  RequestWorkflowWithNodes,
  WorkflowNodeFormWithPopulatedFieldsExternal,
} from './types';
import { TuiAccordion } from '@taiga-ui/kit';
import { TuiElasticContainer } from '@taiga-ui/layout';
import {
  TuiScrollbar,
  TuiDropdown,
  TuiButton,
  TuiDialogService,
} from '@taiga-ui/core';
import { Observable } from 'rxjs';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { RequestProduct } from '@core/types';
import { RequestProductIconComponent } from '@features/requests/request-product-icon/request-product-icon.component';
import { RequestProductIconSize } from '@features/requests/request-product-icon/types';
import { RouterLink } from '@angular/router';
import { EmptyBlockComponent } from '@core/components';
import { WorkflowScreenStateComponent } from './workflow-screen-state/workflow-screen-state.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { RequestWorkflowEventsDialogComponent } from './request-workflow-events-dialog/request-workflow-events-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'x-workflows-list',
  imports: [
    WorkflowHeaderComponent,
    TuiAccordion,
    TuiButton,
    AsyncPipe,
    RequestProductIconComponent,
    RouterLink,
    TuiDropdown,
    TranslatePipe,
    NgClass,
    NgTemplateOutlet,
    EmptyBlockComponent,
    WorkflowStepperComponent,
    TuiScrollbar,
    TuiElasticContainer,
    WorkflowScreenStateComponent,
  ],
  templateUrl: './workflows-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowsListComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(TuiDialogService);
  private readonly requestService = inject(RequestService);
  private readonly workflowService = inject(WorkflowService);
  readonly deviceService = inject(DeviceDetectorService);

  readonly $request = this.requestService.$request;
  readonly $returnLink = signal(
    '/requests/' + this.$request()?.requestNumber + '/summary',
  );
  public $baseRoute = computed(
    () => `/requests/${this.$request()?.requestNumber}`,
  );
  public $requestsWorkflowData = this.workflowService.$entities;
  public $requestWorkflows = signal<RequestWorkflowWithNodes[]>([]);

  readonly $screenState = this.workflowService.$screenState;
  readonly $pendingWork = this.workflowService.$pendingWork;
  /** «Регламентов не найдено» — только для успешно загруженной заявки без регламентов */
  readonly $isEmptyListState = computed(() =>
    ['live', 'stale-offline', 'sync-pending', 'sync-failed'].includes(
      this.$screenState(),
    ),
  );

  retryFailedSync(): void {
    void this.workflowService.retryFailedSync();
  }
  // public $requestProduct = signal<RequestProduct | null>(null);

  constructor() {
    effect(() => {
      if (this.$requestsWorkflowData()) {
        this.$requestWorkflows.set([
          ...this.$requestsWorkflowData()!.requestsWorkflows,
        ]);
      }
    });
  }

  public getWorkflowName(rwfId: string): string {
    return this.workflowService.getWorkFlowName(rwfId);
  }

  @pure
  public getWorkflowNodeForms(
    nodeId: number,
  ): Observable<WorkflowNodeFormWithPopulatedFieldsExternal[]> {
    return this.workflowService.getWorkflowNodeForms(nodeId);
  }

  @pure
  // requestWorkflow.requestProductId — id каталожного продукта, а не ассоциации заявки
  public getRequestProduct(
    productId: number | undefined,
  ): RequestProduct | null {
    if (!productId) {
      return null;
    }

    return (
      this.$request()?.products?.find(
        rp => rp.requestProductId?.id === productId,
      ) || null
    );
  }

  public getNodeValidFormsCount$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<number> {
    return this.workflowService.getNodeValidFormsCount$(
      nodeId,
      requestWorkflowId,
    );
  }

  public allNodeFormsCompleted$(
    nodeId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.workflowService.allNodeFormsCompleted$(
      nodeId,
      requestWorkflowId,
    );
  }

  public someNodeFormsAreInvalid$(
    formId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.workflowService.someNodeFormsAreInvalid$(
      formId,
      requestWorkflowId,
    );
  }

  public formIsInvalid$(
    formId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.workflowService.formIsInvalid$(formId, requestWorkflowId);
  }

  public formIsSubmitted$(
    formId: number,
    requestWorkflowId: string,
  ): Observable<boolean> {
    return this.workflowService.formIsSubmitted$(formId, requestWorkflowId);
  }

  public openWorkflowEventsModal(
    requestWorkflow: RequestWorkflowWithNodes,
  ): void {
    this.dialogService
      .open(new PolymorpheusComponent(RequestWorkflowEventsDialogComponent), {
        closable: true,
        dismissible: true,
        size: null,
        // На мобильном — во весь экран (в v3 диалог на мобильном растягивался сам)
        appearance: this.deviceService.$isMobile() ? 'fullscreen' : 'taiga',
        data: {
          requestNumber: this.$request()!.requestNumber,
          requestWorkflowId: requestWorkflow.id,
          workflowName:
            requestWorkflow.workflowVersion.workflow.name ||
            this.getWorkflowName(requestWorkflow.id),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected readonly RequestProductIconSize = RequestProductIconSize;
}
